import type { SearchKind, SearchResponse } from '../../../api/search';
import {
  NEW_BOOKING_SHORTCUT_KEYS,
  useCreateBookingLauncher,
} from '../../../components/bookings/shared/CreateBookingLauncher';
import {
  NEW_VENUE_SHORTCUT_KEYS,
  useCreateVenueLauncher,
} from '../../../components/dashboards/venues/CreateVenueLauncher';
import {
  NEW_WORKSHOP_SHORTCUT_KEYS,
  useCreateWorkshopLauncher,
} from '../../../components/dashboards/workshops/create/CreateWorkshopLauncher';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { usePermissions } from '../../../hooks/usePermissions';
import type { PaletteCommand, PaletteSection } from '../commandPalette.types';
import { useAppMode } from '../use-app-mode';

import { availableCommands, commandMatches, type CreateActions } from './palette-commands';
import { useRecentSearches } from './recent-searches';
import { recentRows, resultSections } from './result-sections';
import { canOpen, isSearchableTerm, searchableKinds } from './search-kinds';
import { useGlobalSearch } from './use-global-search';

/** `idle` means no search is running for what is typed — too short, or nothing to search. */
export type SearchStatus = 'idle' | 'loading' | 'error' | 'ready';

function useCreateActions(): CreateActions {
  const workshop = useCreateWorkshopLauncher();
  const booking = useCreateBookingLauncher();
  const venue = useCreateVenueLauncher();
  return {
    workshop: {
      allowed: workshop.canCreateWorkshop,
      open: workshop.openCreateWorkshop,
      shortcut: NEW_WORKSHOP_SHORTCUT_KEYS,
    },
    booking: {
      allowed: booking.canCreateBooking,
      open: booking.openCreateBooking,
      shortcut: NEW_BOOKING_SHORTCUT_KEYS,
    },
    venue: {
      allowed: venue.canCreateVenue,
      open: venue.openCreateVenue,
      shortcut: NEW_VENUE_SHORTCUT_KEYS,
    },
  };
}

function section(id: string, title: string, rows: PaletteCommand[]): PaletteSection[] {
  return rows.length > 0 ? [{ id, title, rows }] : [];
}

interface SearchState {
  data?: SearchResponse;
  isError: boolean;
  isPlaceholderData: boolean;
}

function searchStatus(searchable: boolean, search: SearchState): SearchStatus {
  if (!searchable) return 'idle';
  if (search.isError) return 'error';
  if (!search.data || search.isPlaceholderData) return 'loading';
  return 'ready';
}

/** Typed text: the commands it matches, then each result group that found something. */
function typedSections(
  query: string,
  kind: SearchKind | null,
  commands: PaletteCommand[],
  status: SearchStatus,
  search: SearchState,
): PaletteSection[] {
  const matching = kind ? [] : commands.filter((c) => commandMatches(c, query));
  const groups = (search.data?.groups ?? []).filter((g) => !kind || g.kind === kind);
  const showResults = status === 'loading' || status === 'ready';
  return [
    ...section('commands', 'Commands', matching),
    ...(showResults ? resultSections(groups) : []),
  ];
}

/**
 * What the palette lists for the current query and filter.
 *
 * Nothing typed: the viewer's Recent items, then every command. Something
 * typed: the commands it matches, then each result group. With a filter chip
 * picked, only that group — commands are not a result type.
 */
export function usePaletteSections(query: string, kind: SearchKind | null) {
  const { hasAnyPermission } = usePermissions();
  const { mode } = useAppMode();
  const { currentUser } = useCurrentUser();
  const { recent, remember } = useRecentSearches(currentUser?.id ?? null);
  const createActions = useCreateActions();

  const kinds = searchableKinds(hasAnyPermission);
  const searchable = kinds.length > 0 && isSearchableTerm(query);
  const search = useGlobalSearch(searchable ? query : '', kind);
  const commands = availableCommands(createActions, mode, hasAnyPermission);

  if (!query.trim()) {
    const openableRecent = recent.filter((item) => canOpen(item.kind, hasAnyPermission));
    const sections = [
      ...section('recent', 'Recent', recentRows(openableRecent)),
      ...section('commands', 'Commands', commands),
    ];
    return { sections, status: 'idle' as SearchStatus, kinds, remember };
  }

  const status = searchStatus(searchable, search);
  return {
    sections: typedSections(query, kind, commands, status, search),
    status,
    kinds,
    remember,
  };
}
